import type { Tunnels } from "../tunnels/tunnels";
import type { FwaasOverview } from "../overview/overview.types";
import type { FirewallEvents, FirewallEventGroup } from "../events/events.types";
import type { FirewallPolicies, FirewallPolicy } from "../policies/policies.types";
import type { FirewallRouting } from "../routing/routing.types";
import { isNeverUsed } from "../policies/policies.types";
import { eventName, isIpsecEvent } from "../events/eventsFormat";
import { formatCalendarDate, type HaMember } from "kontrolEdge/components/Shared/VmSpecsCard";
import { NO_READING, daysSince, firmwareSummary, serviceLabel, shortDate, shortTime, timeAgo } from "../overview/overviewFormat";

/**
 * What needs attention on one Virtual Firewall (CTX-1208), decided in ONE place: the
 * card, the header's pill, the map's alerts and Device health's pill all read this
 * list, so they cannot disagree. Rules: document "Prototipo v2 del detalle del Virtual
 * Firewall" (P-CTX-95); Device health's levels: CTX-1544.
 */
export type Severity = "critical" | "attention" | "review";

/** One of Device health's six numbers. */
export type HealthStat = "cpu" | "memory" | "sessions" | "uptime" | "ha" | "bgp";

/** The Device health number an item is about, and the few words that card's pill says for it. */
export interface StatRef {
    key: HealthStat;
    pill: string;
}

/** A node of the map an item is about. */
export type AttentionNode = "firewall" | "internet" | "remote" | "site-to-site";

export interface Evidence {
    label: string;
    tone: "neutral" | "ok";
}

export interface AttentionItem {
    id: string;
    severity: Severity;
    title: string;
    detail: string;
    /** The few words the header shows when this is the most serious item: a fact, never a request (CTX-1528). */
    headline: string;
    evidence: Evidence[];
    /** The node it is about: the map turns it amber and the header's pill selects it. `null` for none. */
    node: AttentionNode | null;
    /** Set only by the rules of Device health's six numbers: that card's pill reads nothing else (CTX-1544). */
    stat?: StatRef;
}

export interface AttentionInput {
    overview: FwaasOverview | null;
    tunnels: Tunnels | null;
    events: FirewallEvents | null | undefined;
    policies: FirewallPolicies | null | undefined;
    routing: FirewallRouting | null | undefined;
    /** By role, for which member is down; never shown by serial. */
    haMembers?: HaMember[];
    now?: number;
}

const SEVERITY_ORDER: Record<Severity, number> = { critical: 0, attention: 1, review: 2 };
/** A repeated error earns an item from this many occurrences in the log. */
export const REPEATED_ERROR_MIN = 5;
/** CPU now at or above these reads amber, then red (CTX-1544). */
export const CPU_PCT = { attention: 80, critical: 95 } as const;
/** Memory now at or above these reads amber, then red: from 88% FortiOS goes into conserve mode by default. */
export const MEMORY_PCT = { attention: 80, critical: 88 } as const;
/** A license this close to its end earns an item. */
export const LICENSE_WARNING_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Under a day ago: a restart or a failover this recent earns an item. */
const withinADay = (iso: string, now: number) => now - Date.parse(iso) < DAY_MS;

function daysUntil(date: string, now: number): number | null {
    const at = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00Z` : date);
    return Number.isNaN(at) ? null : Math.ceil((at - now) / DAY_MS);
}

/** "Passive member down": a member by its role, as the map draws the pair. */
function memberEvidence(mode: string, members: HaMember[]): Evidence[] {
    return members
        .filter((member) => member.status !== "up")
        .map((member) => {
            const active = mode === "active-active" || member.role === "primary";
            return { label: `${active ? "Active" : "Passive"} member down`, tone: "neutral" as const };
        });
}

/**
 * High availability at Device health's levels (CTX-1544): a member down is critical; a
 * pair out of sync, or one that failed over in the last day, reads amber. A standalone
 * firewall has no pair to fail.
 */
function haItems({ overview, haMembers = [], now = Date.now() }: AttentionInput): AttentionItem[] {
    const ha = overview?.ha;
    if (!ha || ha.mode === "standalone") return [];
    const membersDown = ha.members_total != null && ha.members_up != null && ha.members_up < ha.members_total;
    const outOfSync = ha.in_sync === false;
    const members = ha.members_total != null && ha.members_up != null ? `${ha.members_up} of ${ha.members_total} members up` : null;
    if (membersDown || outOfSync) {
        return [
            {
                id: "ha",
                severity: membersDown ? "critical" : "attention",
                title: membersDown ? "High availability is degraded" : "HA configuration is out of sync",
                detail: [membersDown ? members : null, outOfSync ? "configuration out of sync" : null].filter(Boolean).join(" · "),
                headline: membersDown ? "High availability is degraded" : "HA is out of sync",
                evidence: [
                    ...memberEvidence(ha.mode, haMembers),
                    ...(ha.last_failover_at ? [{ label: `Last failover ${shortDate(ha.last_failover_at)}`, tone: "neutral" as const }] : []),
                ],
                node: "firewall",
                stat: { key: "ha", pill: membersDown ? "HA degraded" : "HA out of sync" },
            },
        ];
    }
    if (!ha.last_failover_at || !withinADay(ha.last_failover_at, now)) return [];
    const ago = timeAgo(ha.last_failover_at, now);
    return [
        {
            id: "ha-failover",
            severity: "attention",
            title: `HA failed over ${ago}`,
            detail: [members, ha.in_sync ? "in sync" : null].filter(Boolean).join(" · ") || "The pair changed its active member",
            headline: "HA failed over recently",
            evidence: [],
            node: "firewall",
            stat: { key: "ha", pill: `Failed over ${ago}` },
        },
    ];
}

type SiteToSite = Extract<Tunnels["items"][number], { kind: "site-to-site" }>;

/** "To-AWS: phase 2 To-AWS-2 down", or a count when FortiOS names none. */
function phase2Down(tunnel: SiteToSite): string {
    const down = tunnel.phase2.filter((p) => p.status !== "up");
    const names = down.map((p) => p.name).filter((name): name is string => Boolean(name));
    return names.length === down.length && names.length
        ? `${tunnel.name}: phase 2 ${names.join(", ")} down`
        : `${tunnel.name}: ${down.length} of ${tunnel.phase2.length} phase 2 down`;
}

function tunnelItems({ tunnels }: AttentionInput): AttentionItem[] {
    const siteToSite = (tunnels?.items ?? []).flatMap((t) => (t.kind === "site-to-site" ? [t] : []));
    const down = siteToSite.filter((t) => t.status === "down");
    const partial = siteToSite.filter((t) => t.status === "partial");
    const items: AttentionItem[] = [];
    if (down.length) {
        items.push({
            id: "tunnels-down",
            severity: "critical",
            title: down.length === 1 ? `Site-to-site tunnel ${down[0].name} is down` : `${down.length} site-to-site tunnels are down`,
            detail: down.map((t) => t.name).join(", "),
            headline: "Site-to-site VPN is down",
            evidence: [],
            node: "site-to-site",
        });
    }
    if (partial.length) {
        items.push({
            id: "tunnels-partial",
            severity: "attention",
            title: partial.length === 1 ? `Tunnel ${partial[0].name} is partly up` : `${partial.length} tunnels are partly up`,
            detail: partial.map(phase2Down).join(" · "),
            headline: "Site-to-site VPN is partly up",
            evidence: [],
            node: "site-to-site",
        });
    }
    return items;
}

/** BGP at Device health's levels (CTX-1544): partly up reads amber and all down red, as site-to-site tunnels do. */
function bgpItems({ routing }: AttentionInput): AttentionItem[] {
    const bgp = routing?.bgp;
    if (!bgp || bgp.neighbors_total === 0 || bgp.neighbors_up >= bgp.neighbors_total) return [];
    const down = bgp.neighbors_total - bgp.neighbors_up;
    const allDown = bgp.neighbors_up === 0;
    return [
        {
            id: "bgp",
            severity: allDown ? "critical" : "attention",
            title: allDown && down > 1 ? `All ${down} BGP neighbors are down` : `${plural(down, "BGP neighbor")} down`,
            detail: `${bgp.neighbors_up} of ${bgp.neighbors_total} established`,
            headline: allDown ? "BGP is down" : "BGP is partly up",
            evidence: bgp.peer_as.length ? [{ label: `Remote AS ${bgp.peer_as.join(", ")}`, tone: "neutral" }] : [],
            node: "firewall",
            stat: { key: "bgp", pill: allDown ? "BGP down" : "BGP partly up" },
        },
    ];
}

function licenseItems({ overview, now = Date.now() }: AttentionInput): AttentionItem[] {
    const licenses = overview?.licenses;
    if (!licenses) return [];
    const items: AttentionItem[] = [];
    const expiredServices = licenses.services.filter((s) => s.status === "expired");
    const expired = expiredServices.map(serviceLabel);
    const vmInvalid = licenses.vm.status != null && licenses.vm.status !== "valid";
    if (vmInvalid || expired.length) {
        // Which one, and since when where the firewall says.
        const since = [
            ...expiredServices.map((s) => (s.expires_on ? `${serviceLabel(s)} expired on ${formatCalendarDate(s.expires_on)}` : null)),
            vmInvalid ? `VM license: ${licenses.vm.status}` : null,
        ].filter(Boolean);
        items.push({
            id: "licenses-invalid",
            severity: "critical",
            title: expired.length ? `${expired.join(", ")} expired` : "The VM license is not valid",
            detail: since.length ? since.join(" · ") : "Protection the plan includes may be off",
            headline: "A license is not valid",
            evidence: [],
            node: null,
        });
    }
    const ending = [
        { name: "VM license", date: licenses.vm.expires_on },
        { name: "Support", date: licenses.support.expires_on },
        ...licenses.services.filter((s) => s.status === "active").map((s) => ({ name: serviceLabel(s), date: s.expires_on })),
    ].flatMap(({ name, date }) => {
        const days = date ? daysUntil(date, now) : null;
        return date && days != null && days >= 0 && days <= LICENSE_WARNING_DAYS ? [{ name, days, date }] : [];
    });
    if (ending.length) {
        const soonest = Math.min(...ending.map((e) => e.days));
        items.push({
            id: "licenses-ending",
            severity: "attention",
            title: `${ending.map((e) => e.name).join(", ")} ${ending.length === 1 ? "ends" : "end"} in ${plural(soonest, "day")}`,
            detail: `${ending.map((e) => `${e.name} ends on ${formatCalendarDate(e.date)}`).join(" · ")}. Renew before then to keep the protection on`,
            headline: "A license is about to end",
            evidence: [],
            node: null,
        });
    }
    if (licenses.security_updates != null && licenses.security_updates !== "connected") {
        items.push({
            id: "security-updates",
            severity: "attention",
            title: "Security updates are not connected",
            detail: "The firewall is not receiving new protection signatures",
            headline: "Security updates are off",
            evidence: [],
            node: null,
        });
    }
    return items;
}

export const dialUpNames = (tunnels: Tunnels | null) =>
    (tunnels?.items ?? []).flatMap((t) => (t.kind === "dial-up" ? [t.name] : []));

/**
 * Which node a repeated error belongs to. An event that names its tunnel goes to that
 * tunnel's node. A phase 1 failure names none — the negotiation fails before FortiOS
 * knows the tunnel (Skandia, 2026-10-15) — so it goes to remote access when the
 * firewall serves one and no site-to-site tunnel is negotiating; otherwise it is an
 * inbound attempt on the internet side.
 */
export function nodeOfError(group: FirewallEventGroup, tunnels: Tunnels | null): AttentionNode {
    const dialUps = dialUpNames(tunnels);
    if (group.tunnel) return dialUps.includes(group.tunnel) ? "remote" : "site-to-site";
    const negotiating = (tunnels?.items ?? []).some((t) => t.kind === "site-to-site" && t.status !== "up");
    return dialUps.length && !negotiating ? "remote" : "internet";
}

/** Rules that pass through a tunnel: its name is one of their interfaces. */
export const rulesThrough = (policies: FirewallPolicy[], tunnelNames: string[]) =>
    policies.filter((p) => [...p.from, ...p.to].some((name) => name != null && tunnelNames.includes(name)));

function repeatedErrorItems({ events, tunnels, policies, now = Date.now() }: AttentionInput): AttentionItem[] {
    const since = now - DAY_MS;
    const top = (events?.items ?? [])
        .filter((g) => g.level === "error" && g.count >= REPEATED_ERROR_MIN && Date.parse(g.last_at) >= since)
        .sort((a, b) => b.count - a.count)[0];
    if (!top) return [];
    const node = nodeOfError(top, tunnels);
    const ipsec = isIpsecEvent(top.text);
    const title = ipsec
        ? `Repeated IPsec negotiation failures${node === "remote" ? " on remote access" : ""}`
        : `Repeated error: ${eventName(top.text)}`;
    const evidence: Evidence[] = [];
    if (node === "remote") {
        const users = (tunnels?.items ?? []).reduce((sum, t) => (t.kind === "dial-up" ? sum + t.connections : sum), 0);
        evidence.push({ label: plural(users, "user") + " connected", tone: "neutral" });
        const tunnelRules = policies ? rulesThrough(policies.items, dialUpNames(tunnels)) : null;
        const hits = tunnelRules?.reduce((sum, p) => sum + (p.hits ?? 0), 0) ?? null;
        if (hits != null) evidence.push({ label: `Tunnel rules: ${hits.toLocaleString("en-US")} hits`, tone: "neutral" });
        if (users === 0 && hits === 0) evidence.push({ label: "Likely no user impact", tone: "ok" });
    }
    const headline = {
        remote: "Repeated failures on remote access",
        internet: "Inbound IPsec attempts",
        "site-to-site": "Repeated failures on the site-to-site VPN",
        firewall: "Repeated errors on the firewall",
    }[node];
    return [
        {
            id: "repeated-error",
            severity: "attention",
            title,
            detail: `${top.count.toLocaleString("en-US")} times in the recent log, the last one ${timeAgo(top.last_at, now)}`,
            headline,
            evidence,
            node,
        },
    ];
}

/**
 * CPU and memory at Device health's levels, on the number that card shows: now
 * (CTX-1544). The hour's peak stays as evidence; a peak alone no longer earns an item.
 */
function loadItems({ overview }: AttentionInput): AttentionItem[] {
    const resources = overview?.resources;
    if (!resources) return [];
    const sessions = resources.sessions.now;
    const load = [
        { key: "cpu" as const, name: "CPU", usage: resources.cpu, levels: CPU_PCT, high: "High CPU", risk: "Sustained, it slows traffic down" },
        {
            key: "memory" as const,
            name: "Memory",
            usage: resources.memory,
            levels: MEMORY_PCT,
            high: "High memory",
            risk: `From ${MEMORY_PCT.critical}%, the firewall may refuse new connections`,
        },
    ];
    return load.flatMap(({ key, name, usage, levels, high, risk }): AttentionItem[] => {
        if (usage.now == null || usage.now < levels.attention) return [];
        const critical = usage.now >= levels.critical;
        return [
            {
                id: `load-${key}`,
                severity: critical ? "critical" : "attention",
                title: `${name} at ${usage.now}%`,
                detail: risk,
                headline: critical ? `${name} is at its limit` : `${name} is running high`,
                evidence: [
                    ...(usage.peak_1h != null ? [{ label: `Peak ${usage.peak_1h}% in the last hour`, tone: "neutral" as const }] : []),
                    ...(sessions != null ? [{ label: `${sessions.toLocaleString("en-US")} sessions now`, tone: "neutral" as const }] : []),
                ],
                node: "firewall",
                stat: { key, pill: critical ? `${name} at its limit` : high },
            },
        ];
    });
}

/** A restart in the last day, which Device health's Uptime shows in amber (CTX-1544). */
function restartItems({ overview, now = Date.now() }: AttentionInput): AttentionItem[] {
    const since = overview?.uptime?.since;
    if (!since || !withinADay(since, now)) return [];
    const ago = timeAgo(since, now);
    return [
        {
            id: "restart",
            severity: "attention",
            title: `The firewall restarted ${ago}`,
            detail: `Up since ${shortDate(since)}, ${shortTime(since)}`,
            headline: "Restarted recently",
            evidence: [],
            node: "firewall",
            stat: { key: "uptime", pill: `Restarted ${ago}` },
        },
    ];
}

function ruleItems({ policies, overview, now = Date.now() }: AttentionInput): AttentionItem[] {
    if (!policies) return [];
    const unused = policies.items.filter(isNeverUsed).length;
    const off = policies.items.filter((p) => !p.enabled).length;
    if (!unused && !off) return [];
    const days = overview?.uptime ? daysSince(overview.uptime.since, now) : null;
    return [
        {
            id: "rules",
            severity: "review",
            title: [unused ? `${plural(unused, "rule")} never used` : null, off ? `${off} turned off` : null].filter(Boolean).join(", "),
            detail: days != null ? `Usage counted since the last restart, ${plural(days, "day")} ago` : "Usage counted since the last restart",
            headline: "Rules to review",
            evidence: [],
            node: null,
        },
    ];
}

function firmwareItems({ overview }: AttentionInput): AttentionItem[] {
    const firmware = overview?.firmware;
    const update = firmware ? firmwareSummary(firmware).update : null;
    if (!firmware || !update) return [];
    return [
        {
            id: "firmware",
            severity: "review",
            title: `Firmware ${update} is available`,
            detail: `Running ${firmware.current} · ${update} is the newest mature release`,
            headline: "Firmware update available",
            evidence: [],
            node: "firewall",
        },
    ];
}

const RULES = [haItems, tunnelItems, bgpItems, licenseItems, repeatedErrorItems, loadItems, restartItems, ruleItems, firmwareItems];

/** Every item, the most serious first. Empty: nothing needs attention — in what was read (readingCoverage). */
export function needsAttention(input: AttentionInput): AttentionItem[] {
    return RULES.flatMap((rule) => rule(input)).sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/** How much of what the rules look at could be read. */
export type ReadingCoverage = "full" | "partial" | "none";

const OVERVIEW_SECTIONS = ["uptime", "resources", "firmware", "ha", "licenses"] as const;

/**
 * How much of what `needsAttention` looks at could be read (CTX-1116). An empty list
 * means "all clear" only when this is `full`: with FortiManager unreachable every
 * reading is `null`, the rules find nothing, and saying "No observations" or
 * "Healthy" would invent a state nobody read. Readings still on their way count as
 * unread; the page shows them as loading before it asks.
 */
export function readingCoverage({ overview, tunnels, events, policies, routing }: AttentionInput): ReadingCoverage {
    const sources = [...OVERVIEW_SECTIONS.map((key) => overview?.[key] ?? null), tunnels, events, policies, routing];
    const read = sources.filter((source) => source != null).length;
    if (read === 0) return "none";
    return read === sources.length ? "full" : "partial";
}

/** Device health's pill, and how loud each of its numbers out of range reads (CTX-1544). */
export interface DeviceVerdict {
    tone: "healthy" | "unread" | Exclude<Severity, "review">;
    label: string;
    /** Every reason, on hover, when there is more than one. */
    title?: string;
    stats: Partial<Record<HealthStat, Exclude<Severity, "review">>>;
}

const hasStat = (item: AttentionItem): item is AttentionItem & { stat: StatRef } => item.stat != null;

/**
 * Device health's pill, from its own six numbers only (CTX-1544): the items about them,
 * the most serious named and the rest counted. A tunnel down or a license expired is
 * not the device's state and stays in Observations, so a firewall can read "Healthy"
 * beside a critical observation — that is the point: it says where the problem is
 * not. "Healthy" only when all six were read; "No reading" when none was; nothing
 * while reading, or when part was read and that part is fine.
 */
export function deviceVerdict(items: AttentionItem[], { overview, routing }: Pick<AttentionInput, "overview" | "routing">): DeviceVerdict | null {
    if (overview === null) return null;
    const about = items.filter(hasStat);
    if (about.length) {
        const [worst, ...rest] = about;
        const stats: DeviceVerdict["stats"] = {};
        // Most serious first, so each number keeps the loudest level said about it.
        for (const item of about) {
            if (!stats[item.stat.key]) stats[item.stat.key] = item.severity === "critical" ? "critical" : "attention";
        }
        return {
            tone: worst.severity === "critical" ? "critical" : "attention",
            label: rest.length ? `${worst.stat.pill} +${rest.length}` : worst.stat.pill,
            title: rest.length ? about.map((item) => item.stat.pill).join(" · ") : undefined,
            stats,
        };
    }
    const sources = [overview.resources, overview.uptime, overview.ha, routing];
    const read = sources.filter((source) => source != null).length;
    if (read === 0) return { tone: "unread", label: NO_READING, stats: {} };
    return read === sources.length ? { tone: "healthy", label: "Healthy", stats: {} } : null;
}
